import React, { type ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { isCatalogEnabled } from '../../lib/featureFlags';
import { matchesPathSegments } from './navMatch';
import SidebarBrandBlock from './SidebarBrandBlock';
import ShellToggle from './ShellToggle';
import { isSettingsRoute, shellPath, SIDE_LABEL } from './v2Shell';
import {
  AdminIcon, CatalogIcon, FinancesIcon, HomeIcon, OrdersIcon, ProductsIcon, ReportsIcon, ReturnsIcon, SalesIcon, SettingsIcon,
} from './v2NavIcons';
import gbLogo from '../../assets/images/gb-logo.png';

/**
 * The v2 sidebar — the mock's `aside.side` (built-v12), shown only when
 * `isV2PreviewEnabled(user)` (see `useV2Shell`). A 72px navy icon rail that
 * opens to 232px over the page on hover or keyboard focus; a drawer at
 * 860px and below. Order: brand, Home … Reports & downloads, "Set up"
 * (Catalog where the catalog gates allow it, Settings), then Admin for GB
 * staff below a divider. The v1-only entries (Intelligence Suite,
 * Promotions, P&L statement, Reconcile, My Reports) have no link here;
 * their routes stay.
 */
const PAGES: { label: string; href: string; icon: () => ReactElement }[] = [
  { label: 'Home', href: '/home', icon: HomeIcon },
  { label: 'Sales', href: '/sales', icon: SalesIcon },
  { label: 'Products', href: '/products', icon: ProductsIcon },
  { label: 'Returns', href: '/returns', icon: ReturnsIcon },
  { label: 'Orders', href: '/orders', icon: OrdersIcon },
  { label: 'Finances', href: '/finances', icon: FinancesIcon },
  { label: 'Reports & downloads', href: '/reports', icon: ReportsIcon },
];

function SideLink({ href, label, icon: Icon, active }: { href: string; label: string; icon: () => ReactElement; active: boolean }): ReactElement {
  return (
    <Link
      to={href}
      aria-current={active ? 'page' : undefined}
      aria-label={label}
      className={`flex items-center gap-3 whitespace-nowrap rounded-[9px] px-[15px] py-[9px] phone:min-h-tap text-[13px] no-underline transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${
        active ? 'bg-white/[.14] font-medium text-white' : 'text-white/[.72] hover:bg-white/[.06] hover:text-white'
      }`}
    >
      <Icon />
      <span className={SIDE_LABEL}>{label}</span>
    </Link>
  );
}

const SidebarV2: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
  const path = shellPath(useLocation().pathname);
  const { isAdmin, canUseBrandCatalog, canOperateCatalog } = usePermissions();
  // The same gate as the v1 Catalog entry: no greyed or "not yet" entry for anyone else.
  const showCatalog = isCatalogEnabled() && (canOperateCatalog() || canUseBrandCatalog());
  const on = (href: string) => matchesPathSegments(path, href);

  return (
    <aside
      aria-label="Main"
      className={`group/side fixed inset-y-0 left-0 z-40 flex w-[232px] flex-col leading-[1.45] antialiased overflow-hidden bg-brand-navy text-white transition-transform duration-200 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } min-[861px]:w-[72px] min-[861px]:translate-x-0 min-[861px]:transition-[width,box-shadow] min-[861px]:duration-[180ms] min-[861px]:ease-out min-[861px]:focus-within:w-[232px] min-[861px]:focus-within:shadow-[8px_0_24px_rgba(3,26,66,.18)] min-[861px]:hover:w-[232px] min-[861px]:hover:shadow-[8px_0_24px_rgba(3,26,66,.18)]`}
    >
      <div className="flex h-16 flex-none items-center border-b border-white/[.12] px-[21px]">
        <img
          src={gbLogo}
          alt="Global Bridge"
          className="h-[30px] w-auto max-w-none transition-[clip-path] duration-[180ms] min-[861px]:[clip-path:inset(0_56%_0_0)] min-[861px]:group-focus-within/side:[clip-path:inset(0)] min-[861px]:group-hover/side:[clip-path:inset(0)]"
        />
      </div>

      <SidebarBrandBlock />

      <nav className="flex flex-col gap-0.5 overflow-y-auto overflow-x-hidden px-3 py-2">
        {PAGES.map((p) => (
          <SideLink key={p.href} {...p} active={on(p.href)} />
        ))}

        <div className="h-[34px] flex-none whitespace-nowrap px-3 pb-1.5 pt-3.5 text-[11px] text-white/45">
          <span className={SIDE_LABEL}>Set up</span>
        </div>
        {showCatalog && <SideLink href="/catalog" label="Catalog" icon={CatalogIcon} active={on('/catalog')} />}
        {/* The v2 Settings page (Profile, Team, Notifications, Data exports tabs).
            Active on every page Settings covers. */}
        <SideLink href="/settings" label="Settings" icon={SettingsIcon} active={isSettingsRoute(path)} />

        {isAdmin() && (
          <>
            <div data-testid="side-admin-divider" className="my-2 border-t border-white/[.12]" />
            <SideLink href="/admin" label="Admin" icon={AdminIcon} active={on('/admin')} />
          </>
        )}
      </nav>

      {/* The mock's foot slot: the switch to the classic portal (Ari, 2026-10-07). */}
      <ShellToggle to="v1" />
    </aside>
  );
};

export default SidebarV2;
